// src/components/analytics/AircraftDetailPanel.jsx
// Slide-over panel — aircraft detail, owner, movements, fuel.
// Export logic lives in src/components/modal/AircraftExport.js
// UNITS: US (lbs, gallons, USD)

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  X, Plane, User, Fuel, ArrowUpRight, ArrowDownLeft,
  Shield, Loader, AlertCircle, CheckCircle, Clock,
  Phone, Mail, MapPin, FileText, FileSpreadsheet
} from 'lucide-react';
import { triggerExport } from '../modal/AircraftExport';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Formatters (US Units) ───────────────────────────────────────────────────

const fmt$   = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDT  = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};
const fmtLbs = (lbs) => {
  if (lbs == null) return '—';
  const num = Number(lbs);
  return isNaN(num) ? '—' : `${Math.round(num).toLocaleString()} lbs`;
};

const weightBadge = (cls) => ({
  Light:         'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  Medium:        'bg-amber-500/15   text-amber-400   border-amber-500/30',
  Heavy:         'bg-orange-500/15  text-orange-400  border-orange-500/30',
  'Super Heavy': 'bg-red-500/15     text-red-400     border-red-500/30',
}[cls] || 'bg-gray-700/50 text-gray-500 border-gray-600');

const Receipt = ({ size = 16, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" className={className}>
    <polyline points="6 2 3 6 3 20 21 20 21 6 18 2" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="12" y1="6" x2="12" y2="2" />
  </svg>
);

const Section = ({ icon: Icon, title, color = 'text-sky-400', children }) => (
  <div className="border border-gray-700/60 rounded-xl overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-800/60 border-b border-gray-700/40">
      <Icon size={13} className={color} />
      <span className="text-xs font-semibold text-gray-300 uppercase tracking-wide">{title}</span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Info = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-2 py-1">
    <span className="text-[11px] text-gray-500 shrink-0">{label}</span>
    <span className={`text-[11px] text-right ${mono ? 'font-mono text-gray-200' : 'text-gray-300'}`}>
      {value ?? <span className="text-gray-600">—</span>}
    </span>
  </div>
);

const BoolBadge = ({ value, trueLabel = 'Charged', falseLabel = 'Exempt' }) => (
  value
    ? <span className="flex items-center gap-1 text-emerald-400 text-[11px]"><CheckCircle size={11} />{trueLabel}</span>
    : <span className="flex items-center gap-1 text-amber-400  text-[11px]"><Shield      size={11} />{falseLabel}</span>
);

const ExportButtons = ({ data, onExporting }) => {
  const [busy, setBusy] = useState(null);
  const run = async (type) => {
    setBusy(type);
    onExporting?.(true);
    try { await triggerExport(type, data); }
    catch (err) { console.error('Export error:', err); alert('Export failed. Please try again.'); }
    finally { setBusy(null); onExporting?.(false); }
  };
  return (
    <div className="flex items-center gap-1.5">
      <button onClick={() => run('pdf')} disabled={!!busy}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs
                   bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30
                   text-rose-400 hover:text-rose-300 disabled:opacity-40 transition-colors">
        {busy === 'pdf' ? <Loader size={11} className="animate-spin" /> : <FileText size={11} />} PDF
      </button>
      <button onClick={() => run('excel')} disabled={!!busy}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs
                   bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30
                   text-emerald-400 hover:text-emerald-300 disabled:opacity-40 transition-colors">
        {busy === 'excel' ? <Loader size={11} className="animate-spin" /> : <FileSpreadsheet size={11} />} Excel
      </button>
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

const AircraftDetailPanel = ({ tailNumber, onClose }) => {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!tailNumber) return;
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API_BASE}/tracking/aircraft-detail/${encodeURIComponent(tailNumber)}`);
      setData(res.data);
    } catch { setError('Unable to load aircraft details.'); }
    finally { setLoading(false); }
  }, [tailNumber]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);
  if (!tailNumber) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} />

      <div className="fixed right-0 top-0 h-full w-full max-w-lg z-50 flex flex-col
                      bg-gray-900 border-l border-gray-700/80 shadow-2xl
                      animate-slide-in-right overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4
                        bg-gray-800/80 border-b border-gray-700/60 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-500/30 flex items-center justify-center">
              <Plane size={15} className="text-sky-400" />
            </div>
            <div>
              <p className="text-white font-bold font-mono text-base leading-none">{tailNumber}</p>
              {data && <p className="text-[11px] text-gray-500 mt-0.5">
                {data.source === 'registered' ? '✅ Registered Client' : '👤 Visitor'}
              </p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data && !loading && <ExportButtons data={data} onExporting={setExporting} />}
            <button onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-gray-700 flex items-center justify-center
                         text-gray-400 hover:text-white transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        {exporting && (
          <div className="absolute inset-0 bg-gray-900/85 backdrop-blur-sm z-[60]
                          flex flex-col items-center justify-center gap-3">
            <Loader size={28} className="animate-spin text-sky-400" />
            <p className="text-sm text-gray-300">Generating document…</p>
            <p className="text-xs text-gray-500">Loading FBO settings &amp; logo</p>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {loading && <div className="flex items-center justify-center h-40"><Loader size={22} className="animate-spin text-sky-400" /></div>}
          {error   && <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm"><AlertCircle size={15} />{error}</div>}

          {data && !loading && <>
            <Section icon={Plane} title="Aircraft" color="text-sky-400">
              <div className="grid grid-cols-2 gap-x-4">
                <Info label="Type"         value={data.aircraft?.aircraft_type} />
                <Info label="Manufacturer" value={data.aircraft?.manufacturer} />
                <Info label="Model"        value={data.aircraft?.model} />
                <Info label="Engine"       value={data.aircraft?.engine_type} />
                <Info label="MTOW (lbs)" value={
                  data.aircraft?.mtow_kg
                    ? <span className="flex items-center gap-1.5">
                        {fmtLbs(data.aircraft.mtow_kg)}
                        {data.aircraft?.weight_class && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] border ${weightBadge(data.aircraft.weight_class)}`}>
                            {data.aircraft.weight_class}
                          </span>
                        )}
                      </span>
                    : null
                } />
                <Info label="Callsign"  value={data.aircraft?.callsign}    mono />
                <Info label="Home Base" value={data.aircraft?.home_base} />
                <Info label="Tail"      value={data.aircraft?.tail_number} mono />
                {data.source === 'visitor' && <>
                  <Info label="Operator"   value={data.aircraft?.operator_name} />
                  <Info label="First Seen" value={fmtDT(data.aircraft?.first_seen)} />
                  <Info label="Last Seen"  value={fmtDT(data.aircraft?.last_seen)} />
                </>}
              </div>
            </Section>

            {data.source === 'registered' && data.owner && (
              <Section icon={User} title="Owner / Client" color="text-amber-400">
                <div className="space-y-1 mb-3">
                  <p className="text-sm font-semibold text-white">
                    {data.owner.company || `${data.owner.first_name || ''} ${data.owner.last_name || ''}`.trim() || '—'}
                  </p>
                  {data.owner.company && (data.owner.first_name || data.owner.last_name) && (
                    <p className="text-xs text-gray-400">{data.owner.first_name} {data.owner.last_name}</p>
                  )}
                </div>
                <div className="space-y-1.5 mb-3">
                  {data.owner.email && (
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Mail size={11} className="text-gray-600" />
                      <a href={`mailto:${data.owner.email}`} className="hover:text-sky-400 transition-colors">{data.owner.email}</a>
                    </div>
                  )}
                  {(data.owner.phone || data.owner.phone_mobile) && (
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Phone size={11} className="text-gray-600" />
                      {data.owner.phone_mobile || data.owner.phone}
                    </div>
                  )}
                  {data.owner.city && (
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <MapPin size={11} className="text-gray-600" />
                      {[data.owner.city, data.owner.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-700/40">
                  <div className="bg-gray-800/60 rounded-lg p-2.5">
                    <p className="text-[10px] text-gray-500 mb-1">Landing Fee</p>
                    <BoolBadge value={data.owner.charge_landing_fees} />
                  </div>
                  <div className="bg-gray-800/60 rounded-lg p-2.5">
                    <p className="text-[10px] text-gray-500 mb-1">Takeoff Fee</p>
                    <BoolBadge value={data.owner.charge_takeoff_fees} />
                  </div>
                  <div className="bg-gray-800/60 rounded-lg p-2.5">
                    <p className="text-[10px] text-gray-500 mb-1">Fuel Discount</p>
                    <span className="text-amber-400 text-[11px] font-semibold">
                      {data.owner.fuel_discount_pct > 0 ? `−${data.owner.fuel_discount_pct}%` : 'None'}
                    </span>
                  </div>
                  <div className="bg-gray-800/60 rounded-lg p-2.5">
                    <p className="text-[10px] text-gray-500 mb-1">Account Balance</p>
                    <span className={`text-[11px] font-semibold ${(data.owner.balance || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {fmt$(data.owner.balance)}
                    </span>
                  </div>
                </div>
                {data.owner.notes && (
                  <p className="mt-2 text-[11px] text-gray-500 italic bg-gray-800/40 rounded-lg px-3 py-2">{data.owner.notes}</p>
                )}
              </Section>
            )}

            {data.fee_summary && (
              <Section icon={Receipt} title="Fee Summary" color="text-emerald-400">
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-gray-800/60 rounded-lg p-2.5 text-center">
                    <p className="text-[10px] text-gray-500 mb-1">Movements</p>
                    <p className="text-base font-bold text-white">{data.fee_summary.total_movements}</p>
                  </div>
                  <div className="bg-gray-800/60 rounded-lg p-2.5 text-center">
                    <p className="text-[10px] text-gray-500 mb-1">Total Fees</p>
                    <p className="text-base font-bold text-emerald-400">{fmt$(data.fee_summary.total_gross)}</p>
                  </div>
                  <div className="bg-gray-800/60 rounded-lg p-2.5 text-center">
                    <p className="text-[10px] text-gray-500 mb-1">Exempt</p>
                    <p className="text-base font-bold text-amber-400">{data.fee_summary.total_exempt}</p>
                  </div>
                </div>
                {data.fee_summary.fuel_gallons > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <div className="bg-gray-800/60 rounded-lg p-2.5 text-center">
                      <p className="text-[10px] text-gray-500 mb-1">Gallons</p>
                      <p className="text-sm font-bold text-sky-400">{Number(data.fee_summary.fuel_gallons).toFixed(1)}</p>
                    </div>
                    <div className="bg-gray-800/60 rounded-lg p-2.5 text-center">
                      <p className="text-[10px] text-gray-500 mb-1">Fuel Raw</p>
                      <p className="text-sm font-bold text-gray-200">{fmt$(data.fee_summary.fuel_total_raw)}</p>
                    </div>
                    <div className="bg-gray-800/60 rounded-lg p-2.5 text-center">
                      <p className="text-[10px] text-gray-500 mb-1">After Disc.</p>
                      <p className="text-sm font-bold text-emerald-400">{fmt$(data.fee_summary.fuel_total_disc)}</p>
                    </div>
                  </div>
                )}
              </Section>
            )}

            {data.movements?.length > 0 && (
              <Section icon={Clock} title="Recent Movements" color="text-purple-400">
                <div className="space-y-1.5">
                  {data.movements.slice(0, 10).map((m, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 px-2
                                            rounded-lg hover:bg-gray-800/60 transition-colors">
                      <div className="flex items-center gap-2">
                        <span className={`flex items-center gap-1 text-xs font-medium ${
                          m.movement_type === 'TAKEOFF' ? 'text-emerald-400' : 'text-orange-400'
                        }`}>
                          {m.movement_type === 'TAKEOFF'
                            ? <><ArrowUpRight size={11} />Takeoff</>
                            : <><ArrowDownLeft size={11} />Landing</>}
                        </span>
                        {m.weight_class && m.weight_class !== 'Unknown' && (
                          <span className={`px-1.5 py-0.5 rounded text-[9px] border ${weightBadge(m.weight_class)}`}>
                            {m.weight_class}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {m.fee_exempt
                          ? <span className="text-[10px] text-amber-400 flex items-center gap-1"><Shield size={9} />Exempt</span>
                          : m.fee > 0 && <span className="text-[11px] text-emerald-400 tabular-nums font-medium">{fmt$(m.fee)}</span>
                        }
                        <span className="text-[10px] text-gray-500 tabular-nums">{fmtDT(m.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {data.source === 'visitor' && data.aircraft?.fuel_added > 0 && (
              <Section icon={Fuel} title="Fuel" color="text-cyan-400">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold text-white tabular-nums">{Number(data.aircraft.fuel_added).toFixed(1)} gal</p>
                    <p className="text-xs text-gray-500">{data.aircraft.fuel_type || 'Type unknown'}</p>
                  </div>
                  <Fuel size={28} className="text-cyan-400/30" />
                </div>
              </Section>
            )}

            {data.source === 'registered' && data.fuel?.length > 0 && (
              <Section icon={Fuel} title="Fuel Transactions" color="text-cyan-400">
                <div className="space-y-2">
                  {data.fuel.slice(0, 8).map((t, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 px-2
                                            rounded-lg hover:bg-gray-800/60 transition-colors">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                          t.source === 'truck'
                            ? 'bg-sky-500/15 text-sky-400 border-sky-500/30'
                            : 'bg-purple-500/15 text-purple-400 border-purple-500/30'
                        }`}>{t.source === 'truck' ? 'Truck' : 'Tank'}</span>
                        <span className="text-xs text-gray-300">{t.fuel_type}</span>
                        <span className="text-xs text-gray-400 tabular-nums">{Number(t.gallons).toFixed(1)} gal</span>
                      </div>
                      <div className="flex items-center gap-2 text-right">
                        <div>
                          {t.fuel_discount_pct > 0 && (
                            <p className="text-[10px] text-gray-600 line-through tabular-nums">{fmt$(t.total_amount)}</p>
                          )}
                          <p className="text-[11px] text-emerald-400 font-medium tabular-nums">
                            {fmt$(t.total_after_discount ?? t.total_amount)}
                          </p>
                        </div>
                        <span className="text-[10px] text-gray-600 tabular-nums w-20 text-right">{fmtDT(t.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </>}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-700/60 bg-gray-800/40 shrink-0
                        flex items-center justify-between">
          <p className="text-[10px] text-gray-600 italic select-none">© {new Date().getFullYear()} AeroTrick</p>
          <button onClick={onClose}
            className="px-5 py-2 rounded-lg bg-gray-700/60 hover:bg-gray-700
                       text-sm text-gray-300 hover:text-white transition-colors">
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .animate-slide-in-right { animation: slide-in-right 0.25s cubic-bezier(0.16,1,0.3,1); }
      `}</style>
    </>
  );
};

export default AircraftDetailPanel;